import { useUserSettingsStore } from '@/features/settings/userSettingsStore';
import {
  CREDIT_SYMBOL_KEY,
  DEFAULT_CREDIT_SYMBOL,
  DEFAULT_REPORT_LANGUAGE,
  DEFAULT_UI_LANGUAGE,
  LOCALE_KEY,
  REPORT_LANGUAGE_KEY,
  UI_LANGUAGE_KEY,
  type CreditSymbol,
  type ReportLanguage,
  type UiLanguage,
} from './localizationKeys';
import { getIncomingMessageLocale } from './incomingLocale';
import { resolveMessageLocale, type MessageLocale } from './messageLocale';

function browserLocale(): string {
  if (typeof navigator !== 'undefined' && navigator.language) return navigator.language;
  return 'en-US';
}

export function useLocale(): string {
  return useUserSettingsStore((s) => {
    const v = (s.settings as Record<string, unknown>)[LOCALE_KEY];
    return typeof v === 'string' && v ? v : browserLocale();
  });
}

const UI_LANGUAGE_VALUES: readonly string[] = ['auto', 'en', 'fr', 'de', 'es'];

// Shared body for the 'auto'-or-catalog-language settings keys (ui_language,
// report_language): validated read with a fallback, so both preferences stay
// in lockstep when the catalog set or invalid-value handling changes.
function useLanguagePreference(key: string, fallback: UiLanguage): UiLanguage {
  return useUserSettingsStore((s) => {
    const v = (s.settings as Record<string, unknown>)[key];
    return typeof v === 'string' && UI_LANGUAGE_VALUES.includes(v)
      ? (v as UiLanguage)
      : fallback;
  });
}

/** The raw ui_language preference ('auto' when unset/invalid). */
export function useUiLanguage(): UiLanguage {
  return useLanguagePreference(UI_LANGUAGE_KEY, DEFAULT_UI_LANGUAGE);
}

/** The raw report_language preference ('auto' when unset/invalid). */
export function useReportLanguage(): ReportLanguage {
  return useLanguagePreference(REPORT_LANGUAGE_KEY, DEFAULT_REPORT_LANGUAGE);
}

/**
 * The message-catalog language the UI should render in. Precedence:
 *   1. an explicit ui_language preference (the user chose it — always wins);
 *   2. a `?lang=` handoff from the marketing site (see incomingLocale) — so an
 *      existing German visitor lands on a German register/login page;
 *   3. on 'auto', the language prefix of the formatting locale (the
 *      pre-ui_language behaviour, so existing users see no change).
 */
export function useMessageLanguage(): MessageLocale {
  const uiLanguage = useUiLanguage();
  const locale = useLocale();
  if (uiLanguage !== 'auto') return uiLanguage;
  return getIncomingMessageLocale() ?? resolveMessageLocale(locale);
}

/**
 * The report language surfaces should preselect: the explicit report_language
 * preference, or — on 'auto' — the language of the formatting locale (the
 * pre-setting behaviour, so existing users see no change until they pick one).
 * Deliberately independent of ui_language: report language is about generated
 * content, not chrome.
 */
export function useReportLanguageDefault(): MessageLocale {
  const reportLanguage = useReportLanguage();
  const locale = useLocale();
  return reportLanguage === 'auto' ? resolveMessageLocale(locale) : reportLanguage;
}

export function useCreditSymbol(): CreditSymbol {
  return useUserSettingsStore((s) => {
    const v = (s.settings as Record<string, unknown>)[CREDIT_SYMBOL_KEY];
    return v === 'cr' || v === '' || v === '•' ? (v as CreditSymbol) : DEFAULT_CREDIT_SYMBOL;
  });
}
